import { Accordion, Badge, Button, Form, ProgressBar } from 'react-bootstrap';
import { ColorSetting, WootMappedIAGDetails, OrdersWootTableRow, WootMappedOrderDetails, WootReturnExcelRow, MissionControlIA, MissionControlPriceBookEntry } from '../../WootOrdersImport.interfaces';
import { apiFetch } from '../../../utility/fetch';
import { useCallback, useMemo, useState } from 'react';
import { UserData } from '../../../user/UserContext';
import { useLoading } from '../../../loading/LoadingContext';
import { getSalesforceUrlByWindowLocation } from '../../../common/common.entities';
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError';
import { Check, CloudUpload, Exclamation } from 'react-bootstrap-icons';
import { FileUploader } from 'react-drag-drop-files';
import { DateTime } from 'luxon';
import WootFbaJourneyStep from './WootFbaJourneyStep';
import { excelToJson, getTryCatchError } from '../../../common/common.helpers';
import { Inventory__c, MissionControlOrderData } from '../../../common/common.interfaces';
import { mapWootOrderFromAllocationAndReturn, mapWootReturnIAGs } from '../../woot.fba.helpers';
import { ContentItem, SalesforceCreateParentChildRequestBody } from '../../../common/common.api.interfaces';

interface WootFbaJourneyReturnProps {
  row: OrdersWootTableRow
  returnColors: ColorSetting
  handleDownloadFile: any
  handlePreviewFile: any
  userData: UserData
  setLastRefresh: any
}

export default function WootFbaJourneyReturnPO(props: WootFbaJourneyReturnProps) {
  const {changeStep, changeSubStep, toggleLoading} = useLoading()
  const [wrpoNumber, setWrpoNumber]: [string, any] = useState(undefined)
  const [excelFile, setExcelFile]: [File, any] = useState(undefined)
  const [pdfFile, setPdfFile]: [File, any] = useState(undefined)
  const [returnError, setReturnError]: [CustomErrorProps, any] = useState(undefined)
  const WOOT_FBA_CB_PATH = 'crystal-ball/woot/fba'

  // #region WRPO NUMBER
  const handleSaveWrpoNumber = useCallback(async (wrpoNumber: string, row: OrdersWootTableRow) => {
    setReturnError({
      visible: false
    })
    if(!wrpoNumber || wrpoNumber.trim().length === 0){
      setReturnError({
        closeError: setReturnError({visible: false}),
        content: undefined,
        description: `Failed to save WRPO Number to Journey. Please enter a valid WRPO Number in the text box provided.`,
        title: 'JOURNEY WRPO NUMBER SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(true)
    try{
      changeStep('Saving Journey')
      changeSubStep('Saving WRPO Number to Journey')
      const response = await apiFetch({
        endpoint: 'azureTables',
        method: 'POST',
        accessToken: props.userData.accessToken,
        body: JSON.stringify({
          table: 'ordersWoot',
          records: [{
            partitionKey: row.partitionKey,
            rowKey: row.rowKey,
            wrpoNumber: wrpoNumber
          }]
        })
      })
      if(!response.ok){
        setReturnError({
          closeError: setReturnError({visible: false}),
          content: JSON.stringify({status: response.status, statusText: response.statusText, body: response.body}),
          description: `Failed to save WRPO Number to Journey. Contact an administrator for assistance.`,
          title: 'JOURNEY SAVE FAILED',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
    }catch(error){
      setReturnError({
        closeError: setReturnError({visible: false}),
        content: JSON.stringify(error),
        description: `Failed to save WRPO Number to Journey. Contact an administrator for assistance.`,
        title: 'JOURNEY SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    props.setLastRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [props.userData])

  // #region Map IAG
  const handleMapReturnIag = useCallback(async (row: OrdersWootTableRow) => {
    setReturnError(undefined)

    changeStep('Mapping Return IAGs')

    toggleLoading(true)
    changeSubStep(`Loading Source WRPO JSON ${row.sourceWrpoJsonPath}`)
    const response = await apiFetch<WootReturnExcelRow[]>({
      endpoint: 'blob',
      method: 'GET',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: row.sourceWrpoJsonPath}
      ]
    })
    if(!response.ok){
      setReturnError({
        content: JSON.stringify(response.body),
        description: `Failed to get WRPO JSON from Azure.`,
        title: 'Return IAG Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Determining ASIN Uniques for Inventory Query')
    const uniqueASIN = []
    const returnJson = response.body

    for(const row of returnJson){
      const asin = row.ASIN?.trim()

      if(asin?.length > 0){
        if(!uniqueASIN.includes(asin)){
          uniqueASIN.push(asin)
        } 
      }
    }

    changeSubStep('Getting Product Inventory')
    const asinIn = `'${uniqueASIN.join(`','`)}'`
    const soql = `SELECT Id, Name, Fulfillable_Inventory__c, Product__c, Product__r.MPN__c, Product__r.ASIN__c, Warehouse__r.Name FROM Inventory__c WHERE Product__r.ASIN__c IN (${asinIn}) AND Warehouse__r.Name IN ('FBA Woot', 'FBA US') AND Product__r.RecordType.Name = 'Product'`
    const inventoryResponse = await apiFetch<{records: Inventory__c[]}>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        soql
      })
    })
    if(!inventoryResponse.ok){
      setReturnError({
        content: JSON.stringify(inventoryResponse.body),
        description: `Failed to get Inventory from Mission Control.`,
        title: 'Return IAG Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Mapping IAGs')
    const mapped = mapWootReturnIAGs(row, returnJson, inventoryResponse.body.records)
    
    changeSubStep('Saving Mapping Results')
    const saveResultBlobName = `${WOOT_FBA_CB_PATH}/${row.poNumber}/${row.poNumber}-return-mapped.json`
    const saveResultResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(mapped),
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: saveResultBlobName}
      ]
    })
    if(!saveResultResponse.ok){
      setReturnError({
        content: JSON.stringify(saveResultResponse.body),
        description: `Failed to save IAG mapped results file.`,
        title: 'Return IAG Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    changeSubStep('Saving Journey')
    //only modify the field in question in case other are modifying it
    const saveJourneyResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [
          {
            partitionKey: row.partitionKey, 
            rowKey: row.rowKey, 
            returnIagMappedPath: saveResultBlobName
          } as OrdersWootTableRow
        ]
      })
    })
    if(!saveJourneyResponse.ok){
      setReturnError({
        content: JSON.stringify(saveJourneyResponse.body),
        description: `Failed to save IAG mapped results record.`,
        title: 'Return IAG Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    props.setLastRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))

    toggleLoading(false)
  }, [props.userData])

  // #region Create IAG
  const handleReturnIagCreate = useCallback(async (row: OrdersWootTableRow) => {
    setReturnError(undefined)

    changeStep('Mapping Return IAGs')

    toggleLoading(true)
    changeSubStep(`Loading Mapped Return IAG ${row.returnIagMappedPath}`)
    const mappedResponse = await apiFetch<WootMappedIAGDetails>({
      endpoint: 'blob',
      method: 'GET',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: row.returnIagMappedPath}
      ]
    })
    if(!mappedResponse.ok){
      setReturnError({
        content: JSON.stringify(mappedResponse.body),
        description: `Failed to get Mapped Return IAG from Azure.`,
        title: 'Return IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Sending data to Mission Control to create IAGs now.`)
    const contents: ContentItem[] = []
    for(const item of mappedResponse.body.iagContent){
      contents.push({
        parent: item.iag,
        children: item.ias
      })
    }
    const requestBody: SalesforceCreateParentChildRequestBody = {
      childObjectName: 'Inventory_Adjustment__c',
      childObjectParentIdField: 'Inventory_Adjustment_Group__c',
      content: contents,
      parentObjectName: 'Inventory_Adjustment_Group__c'
    }
    const response = await apiFetch({
      endpoint: 'salesforceCreateParentChild',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(requestBody)
    })
    if(!response.ok){
      setReturnError({
        content: JSON.stringify(response.body),
        description: `Failed to create Return Inventory Adjustment Groups.`,
        title: 'Return IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving response data from Mission Control.`)
    const responsePath = `${WOOT_FBA_CB_PATH}/${row.poNumber}/${row.poNumber}-iag-return-responses.json`
    const saveResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(response.body),
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: responsePath}
      ],
    })
    if(!saveResponse.ok){
      setReturnError({
        content: JSON.stringify(saveResponse.body),
        description: `Failed save Return Inventory Adjustment Groups response.`,
        title: 'Return IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving Journey`)
    const updateResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          returnIagResponsePath: responsePath,
        } as OrdersWootTableRow]
      })
    })
    if(!updateResponse.ok){
      setReturnError({
        content: JSON.stringify(updateResponse.body),
        description: `Failed update Journey record.`,
        title: 'Return IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Gettings IAGs`)

    const iagResponse = await apiFetch<any>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        soql: `SELECT Id, Shipment_Name__c, Status__c FROM Inventory_Adjustment_Group__c WHERE Shipment_Name__c LIKE 'Woot FBA Flip Return ${row.wrpoNumber}%'`
      })
    })
    if(!iagResponse.ok){
      setReturnError({
        content: JSON.stringify(iagResponse.body),
        description: `Failed getting IAGs from Mission Control.`,
        title: 'Return IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving Journey`)
    const returnIags = iagResponse.body.records.map(r => {return {Id: r.Id, Shipment_Name__c: r.Shipment_Name__c, Status__c: r.Status__c}})

    const updateResponse2 = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          returnIags: JSON.stringify(returnIags)
        }]
      })
    })
    if(!updateResponse2.ok){
      setReturnError({
        content: JSON.stringify(updateResponse2.body),
        description: `Failed update Journey record.`,
        title: 'Return IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    props.setLastRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))

    toggleLoading(false)
  }, [props.userData, props.row])

  // #region MAP ORDER
  const handleMapReturnOrder = useCallback(async (row: OrdersWootTableRow) => {
    setReturnError(undefined)

    changeStep('Mapping Order')

    toggleLoading(true)
    changeSubStep(`Loading Source WRPO JSON for pricing ${row.sourceWrpoJsonPath}`)
    const jsonResponse = await apiFetch<WootReturnExcelRow[]>({
      endpoint: 'blob',
      method: 'GET',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: row.sourceWrpoJsonPath}
      ]
    })
    if(!jsonResponse.ok){
      setReturnError({
        content: JSON.stringify(jsonResponse.body),
        description: `Failed to get WRPO JSON from Azure. ${row.sourceWrpoJsonPath}`,
        title: 'Order Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Loading IAs from Allocation & Return IAG`)
    const allocationIds = JSON.parse(row.allocationIags).map(iag => iag.Id)
    const returnIds = JSON.parse(row.returnIags).map(iag => iag.Id)
    const bothIds = [...allocationIds, ...returnIds]
    const idsIn = `'${bothIds.map(id => id).join(`','`)}'`

    const iaResponse = await apiFetch<{records: MissionControlIA[]}>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        soql: `SELECT Id, Main_SKU__c, Main_SKU__r.ASIN__c, Inventory_Adjustment_Group__c, Inventory_Adjustment_Group__r.Shipment_Name__c, Quantity__c, Source_Inventory__c, Source_Inventory__r.Product__c FROM Inventory_Adjustment__c WHERE Inventory_Adjustment_Group__c IN (${idsIn})`
      })
    })
    if(!iaResponse.ok){
      setReturnError({
        content: JSON.stringify(iaResponse.body),
        description: `Failed to get Inventory Adjustments from Mission Control.`,
        title: 'Order Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Determining PriceBook Entries')
    const uniqueProductIds: string[] = []
    for(const ia of iaResponse.body.records){
      if(!uniqueProductIds.includes(ia.Main_SKU__c)){
        uniqueProductIds.push(ia.Main_SKU__c)
      }
    }

    const productIdsIn = `'${uniqueProductIds.map(id => id).join(`','`)}'`
    changeSubStep('Getting PriceBook Entries')
    const pricebookResponse = await apiFetch<{records: MissionControlPriceBookEntry[]}>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        soql: `SELECT Id, Product2Id FROM PricebookEntry WHERE Product2Id IN (${productIdsIn})`
      })
    })
    if(!pricebookResponse.ok){
      setReturnError({
        content: JSON.stringify(pricebookResponse.body),
        description: `Failed to get Pricebook Entries from Mission Control.`,
        title: 'Order Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Mapping Order')
    let mapped = undefined
    let allocationIas = undefined
    let returnIas = undefined
    try {
      allocationIas = iaResponse.body.records.filter(ia => allocationIds.includes(ia.Inventory_Adjustment_Group__c))
      returnIas = iaResponse.body.records.filter(ia => returnIds.includes(ia.Inventory_Adjustment_Group__c))
      mapped = mapWootOrderFromAllocationAndReturn(row, allocationIas, returnIas, jsonResponse.body, pricebookResponse.body.records, window.location.href)
    }catch(error){
      setReturnError({
        content: JSON.stringify(getTryCatchError(error)),
        description: `Failed to map order.`,
        title: 'Order Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Saving Mapped Order')
    const returnOrderMappedPath = `${WOOT_FBA_CB_PATH}/${row.poNumber}/${row.poNumber}-order-mapped.json`
    const saveBlobResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(mapped),
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: returnOrderMappedPath}
      ]
    })
    if(!saveBlobResponse.ok){
      setReturnError({
        content: JSON.stringify(saveBlobResponse.body),
        description: `Failed to save mapped order to Azure.`,
        title: 'Order Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Saving Journey')
    const journeyResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          returnOrderMappedPath
        } as OrdersWootTableRow]
      })
    })
    if(!journeyResponse.ok){
      setReturnError({
        content: JSON.stringify(journeyResponse.body),
        description: `Failed to save journey to Azure.`,
        title: 'Order Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    props.setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))

  }, [props.userData])

  // #region CREATE ORDER
  const handleReturnOrderCreate = useCallback(async (row: OrdersWootTableRow) => {
    setReturnError({
      visible: false
    })
    changeStep('Sending Order to Mission Control')
    changeSubStep('Loading Mapped Order')

    toggleLoading(true)
    const mappedResponse = await apiFetch<WootMappedOrderDetails>({
      endpoint: 'blob',
      method: 'GET',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: row.returnOrderMappedPath}
      ]
    })
    if(!mappedResponse.ok){
      setReturnError({
        content: JSON.stringify(mappedResponse.body),
        description: `Failed to load mapped order.`,
        title: 'Return Order Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Creating Order')
    const contents: ContentItem[] = []
    contents.push({
      parent: mappedResponse.body.order,
      children: mappedResponse.body.orderItems
    })
    const requestBody: SalesforceCreateParentChildRequestBody = {
      childObjectName: 'OrderItem',
      childObjectParentIdField: 'OrderId',
      content: contents,
      parentObjectName: 'Order'
    }
    const salesforceResponse = await apiFetch({
      endpoint: 'salesforceCreateParentChild',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(requestBody)
    })
    if(!salesforceResponse.ok){
      setReturnError({
        content: JSON.stringify(salesforceResponse.body),
        description: `Failed to create Return Order.`,
        title: 'Return Order Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Saving Order Response')
    const responsePath = `${WOOT_FBA_CB_PATH}/${row.poNumber}/${row.poNumber}-order-response.json`
    const responseSave = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: responsePath}
      ],
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(salesforceResponse.body)
    })
    if(!responseSave.ok){
      setReturnError({
        content: JSON.stringify(responseSave.body),
        description: `Failed to create Return Order.`,
        title: 'Return Order Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Querying for Order')
    const orderResponse = await apiFetch<{records: MissionControlOrderData[]}>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        soql: `SELECT Id, Name FROM Order WHERE Name LIKE '${mappedResponse.body.order.Name}%'`
      })
    })
    if(!orderResponse.ok){
      setReturnError({
        content: JSON.stringify(responseSave.body),
        description: `Failed to query Return Order from Mission Control.`,
        title: 'Return Order Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    const order = JSON.stringify({Id: orderResponse.body.records[0].Id, Name: orderResponse.body.records[0].Name})

    changeSubStep('Updating Journey')
    const recordResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          returnOrderResponsePath: responsePath,
          returnStatus: 'complete',
          returnOrder: order,
          invoiceStatus: 'pending'
        } as OrdersWootTableRow]
      })
    })
    if(!recordResponse.ok){
      setReturnError({
        content: JSON.stringify(recordResponse),
        description: `Failed to save the record to the ordersWoot table.`,
        title: 'JOURNEY SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    props.setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [props.userData])

  // #region SAVE FILES
  const handleSaveReturnFiles = useCallback(async (row: OrdersWootTableRow) => {
    setReturnError({
      visible: false
    })
    changeStep('Saving Return PO Files')
    toggleLoading(true)
    if(!excelFile || !pdfFile){
      setReturnError({
        closeError: setReturnError({visible: false}),
        content: `PDF is ${pdfFile ? 'valid' : 'invalid'}. Excel is ${excelFile ? 'valid' : 'invalid'}.`,
        description: `Failed to save return files to Journey. Please upload valid PDF and Excel files.`,
        title: 'JOURNEY FILE SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    const baseBlobPath = `${WOOT_FBA_CB_PATH}/${props.row.poNumber}`
    const pdfPath = `${baseBlobPath}/${pdfFile.name}`
    const excelPath = `${baseBlobPath}/${excelFile.name}`

    changeSubStep(`Saving PDF to ${pdfPath}`)
    const pdfResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: pdfPath}
      ],
      headers: {
        'Content-Type': 'application/pdf'
      },
      body: pdfFile
    })
    if(!pdfResponse.ok){
      setReturnError({
        content: JSON.stringify(pdfResponse),
        description: `Failed to save the return PDF.`,
        title: 'JOURNEY FILE SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving Excel to ${excelPath}`)
    const excelResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: excelPath}
      ],
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      },
      body: excelFile
    })
    if(!excelResponse.ok){
      setReturnError({
        content: JSON.stringify(excelResponse),
        description: `Failed to save the return Excel.`,
        title: 'JOURNEY FILE SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep('Converting Excel to JSON')
    let rows = undefined
    let jsonPath = undefined
    try {
      jsonPath = `${baseBlobPath}/${excelFile.name.replace('.xlsx', '.json')}`
      rows = await excelToJson<WootReturnExcelRow>(excelFile)
      console.log(rows)
    }catch(error){
      setReturnError({
        content: JSON.stringify(error),
        description: `Failed to convert the Excel to JSON.`,
        title: 'JOURNEY FILE MAPPING FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving mapped JSON to ${jsonPath}`)
    const jsonResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: jsonPath}
      ],
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(rows)
    })
    if(!jsonResponse.ok){
      setReturnError({
        content: JSON.stringify(jsonResponse),
        description: `Failed to save the converted JSON file.`,
        title: 'JOURNEY MAPPING SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    const update = {
      partitionKey: row.partitionKey,
      rowKey: row.rowKey,
      sourceWrpoPdfPath: pdfPath,
      sourceWrpoExcelPath: excelPath,
      sourceWrpoJsonPath: jsonPath
    }
    const recordResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [update]
      })
    })
    if(!recordResponse.ok){
      setReturnError({
        content: JSON.stringify(recordResponse),
        description: `Failed to save the record to the ordersWoot table.`,
        title: 'JOURNEY SAVE FAILED',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    setPdfFile(undefined)
    setExcelFile(undefined)
    toggleLoading(false)
    props.setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [excelFile, pdfFile, props.row])

  const getReturnIags = useCallback(async (row: OrdersWootTableRow) => {
    //TODO
  }, [])

  const handleCloseReturnError = useCallback((e) => {
    setReturnError({
      visible: false
    })
  }, [])

  const handleWrpoNumberChanged = useCallback((e) => {
    const value = e.currentTarget.value.trim().toUpperCase()
    if(!value){
      setWrpoNumber(undefined)
      return
    }
    setWrpoNumber(value)
  }, [])

  // #region VIEW
  const view = useMemo(() => {
    const {row, returnColors} = props  
    const steps = []

    let complete = 0
    let pending = 0
    if(!row.wrpoNumber){
      steps.push('STORE WRPO NUMBER')
      pending += 1
    } else {
      complete += 1
    }
    if(!row.sourceWrpoExcelPath || !row.sourceWrpoPdfPath){
      steps.push('SAVE REQUIRED FILES')
      pending += 1
    } else {
      complete += 1
    }

    if(!row.returnIagMappedPath){
      steps.push('MAP RETURN IA GROUPS')
      pending += 1
    } else {
      complete += 1
    }
    if(!row.returnIags){
      steps.push('CREATE IA GROUPS')
      pending += 1
    } else {
      complete += 1
    }

    if(!row.returnOrderMappedPath){
      steps.push('MAP RETURN ORDER')
      pending += 1
    }  else {
      complete += 1
    }   
    if(!row.returnOrderResponsePath){
      steps.push('CREATE RETURN ORDER')
      pending += 1
    } else {
      complete += 1
    }

    const stepsMax = 6
    const completeProgress = (complete / stepsMax) * 100

    let warning = 0
    if(completeProgress < 100){
      warning = 1
      pending -= 1
    }

    const pendingProgress = (pending / stepsMax) * 100
    const warningProgress = (warning / stepsMax) * 100
    return <>
    <Accordion defaultActiveKey={['pending', 'error'].includes(row.returnStatus) ? 'return' : null} className='custom-accordion'>
      <Accordion.Item eventKey='return'>
        <Accordion.Header className='sticky-row-2'>
          <span className='beacon-header'>RETURN PO IAG & ORDER</span>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <span><Badge bg={returnColors.bg} text={returnColors.fg}>{row.returnStatus?.toUpperCase() ?? 'NOT STARTED'}</Badge></span>
        </Accordion.Header>
        <Accordion.Body>
          <div>
            <div style={{textAlign: 'center'}}>
              <span className='beacon-label'>Steps {complete}/{stepsMax} Complete</span>
              <ProgressBar>
                <ProgressBar animated variant='success' now={completeProgress}/>
                <ProgressBar animated variant='warning' now={warningProgress} />
                <ProgressBar animated variant='secondary' now={pendingProgress} />
              </ProgressBar>
            </div>
            <div>
            {steps.length > 0 && 
                <div className='beacon-header'>NEXT STEP {
                  steps[0] === 'STORE WRPO NUMBER' ? <Button disabled={!row.allocationIags} variant='success' onClick={async () => {await handleSaveWrpoNumber(wrpoNumber, row)}} title='Saves the WRPO Number entered and starts the Return PO process.'>{steps[0]}</Button> :
                  steps[0] === 'SAVE REQUIRED FILES' ? <Button variant='success' onClick={async () => {await handleSaveReturnFiles(row)}} title='Saves the WRPO related files if they have both been uploaded.'>{steps[0]}</Button> :
                  steps[0] === 'MAP RETURN IA GROUPS' ? <Button variant='success' onClick={async () => {await handleMapReturnIag(row)}} title='Maps IA Groups and provides feedback on any issues encountered.'>{steps[0]}</Button> : 
                  steps[0] === 'CREATE IA GROUPS' ? <Button variant='success' onClick={async () => {await handleReturnIagCreate(row)}} title='Creates mapped IA Groups in Mission Control.'>{steps[0]}</Button> : 
                  steps[0] === 'MAP RETURN ORDER' ? <Button variant='success' onClick={async () => {await handleMapReturnOrder(row)}} title='Maps Order and provides feedback on any issues encountered.'>{steps[0]}</Button> : 
                  steps[0] === 'CREATE RETURN ORDER' ? <Button variant='success' onClick={async () => {await handleReturnOrderCreate(row)}} title='Creates mapped Order in Mission Control.'>{steps[0]}</Button> : null
                }</div>
              }
            </div>
            <div>
              <span className='beacon-header'>REQUIREMENTS</span>
              <div style={{display: 'flex', flexWrap: 'wrap'}}>
                {/* WRPO NUMBER */}
                {!row.wrpoNumber && <div className='journey-step warning'>
                  <div className='journey-icon'>
                    <Exclamation height={32} width={32} />
                  </div>
                  <div className='journey-title'>WRPO NUMBER</div>
                  <div>
                    {!row.allocationIags && 'Complete Allocation PO First!'}
                    {row.allocationIags && <Form.Control type='text' placeholder='eg. WRPO17592' value={wrpoNumber} onChange={handleWrpoNumberChanged}/>}
                  </div>
                </div>}
                {row.wrpoNumber && <div className='journey-step success'>
                  <div className='journey-icon'>
                    <Check height={32} width={32} />
                  </div>
                  <div className='journey-title'>WRPO NUMBER</div>
                  <div className='journey-subtext'>
                    {row.wrpoNumber}
                  </div>
                </div>}
                {/* WRPO PDF UPLOAD */}
                {!row.sourceWrpoPdfPath && !pdfFile && <div>
                  <FileUploader disabled={!row.wrpoNumber} 
                    handleChange={(file: File) => (setPdfFile(file))} 
                    name="file" 
                    types={['pdf']}
                  >
                    <div className={`journey-step ${row.wrpoNumber ? 'warning' : 'disabled'}`} style={{cursor: row.wrpoNumber ? 'pointer' : 'not-allowed'}}>
                      <div className="journey-title">UPLOAD RETURN PDF</div>
                      <CloudUpload height={50} width={50} />
                      <div className="journey-subtext">{!row.wrpoNumber ? 'STORE WRPO NUMBER FIRST' : 'Drag & Drop / Click'}</div>
                    </div>

                  </FileUploader>
                </div>}
                {pdfFile && <div>
                  <div className={`journey-step ${row.wrpoNumber ? 'warning' : 'disabled'}`} style={{cursor: row.wrpoNumber ? 'pointer' : 'not-allowed'}}>
                    <div className="journey-title">UPLOAD RETURN PDF</div>
                    <CloudUpload height={50} width={50} />
                    <div className="journey-subtext">{pdfFile.name}</div>
                  </div>
                </div>}
                {row.sourceWrpoPdfPath && <div>
                  <WootFbaJourneyStep 
                    handleDownloadFile={props.handleDownloadFile}
                    handlePreviewFile={props.handlePreviewFile}
                    isPreviewable={false}
                    isSuccess={true}
                    item={row.sourceWrpoPdfPath}
                    itemRequiresParse={false}
                    title='RETURN PDF'
                  />
                </div>}
                {/* WRPO EXCEL UPLOAD */}
                {!row.sourceWrpoExcelPath && !excelFile && <div>
                  <FileUploader disabled={!row.wrpoNumber}
                    handleChange={(file: File) => (setExcelFile(file))} 
                    name="file" 
                    types={['xlsx']}
                  >
                    <div className={`journey-step ${row.wrpoNumber ? 'warning' : 'disabled'}`} style={{cursor: row.wrpoNumber ? 'pointer' : 'not-allowed'}}>
                      <div className="journey-title">UPLOAD RETURN EXCEL</div>
                      <CloudUpload height={50} width={50} />
                      <div className="journey-subtext">{!row.wrpoNumber ? 'STORE WRPO NUMBER FIRST' : 'Drag & Drop / Click'}</div>
                    </div>

                  </FileUploader>
                </div>}
                {excelFile && <div>
                  <div className={`journey-step ${row.wrpoNumber ? 'warning' : 'disabled'}`} style={{cursor: row.wrpoNumber ? 'pointer' : 'not-allowed'}}>
                    <div className="journey-title">UPLOAD RETURN EXCEL</div>
                    <CloudUpload height={50} width={50} />
                    <div className="journey-subtext">{excelFile.name}</div>
                  </div>
                </div>}
                {row.sourceWrpoExcelPath && <div>
                  <WootFbaJourneyStep 
                    handleDownloadFile={props.handleDownloadFile}
                    handlePreviewFile={props.handlePreviewFile}
                    isPreviewable={false}
                    isSuccess={true}
                    item={row.sourceWrpoExcelPath}
                    itemRequiresParse={false}
                    title='RETURN EXCEL'
                  />
                </div>}
                {/* WOOT RETURN PO JSON */}
                {!row.sourceWrpoJsonPath && <div>
                  <div className={`journey-step ${row.wrpoNumber && row.sourceWrpoExcelPath ? 'warning' : 'disabled'}`}>
                    <div className='journey-icon'><Exclamation height={50} width={50} /></div>
                    <div className="journey-title">RETURN JSON</div>
                    <div className="journey-subtext">Pending Excel Upload</div>
                  </div>
                </div>}
                {row.sourceWrpoJsonPath && <div>
                  <WootFbaJourneyStep 
                    handleDownloadFile={props.handleDownloadFile}
                    handlePreviewFile={props.handlePreviewFile}
                    isPreviewable={true}
                    isSuccess={true}
                    item={row.sourceWrpoJsonPath}
                    itemRequiresParse={false}
                    title='RETURN JSON'
                  />
                </div>}
              </div>
            </div>
            <div className='beacon-header'>RESULTS</div>
            <div style={{display: 'flex', flexWrap: 'wrap'}}>
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                isSuccess={typeof row.returnIagMappedPath === 'string'}
                item={row.returnIagMappedPath}
                itemRequiresParse={false}
                title='MAPPED IA GROUPS'
              />
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                isSuccess={typeof row.returnIagResponsePath === 'string'}
                item={row.returnIagResponsePath}
                itemRequiresParse={false}
                title='CREATED IA GROUPS'
              />
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                isSuccess={typeof row.returnOrderMappedPath === 'string'}
                item={row.returnOrderMappedPath}
                itemRequiresParse={false}
                title='MAPPED ORDER'
              />
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                isSuccess={typeof row.returnOrderResponsePath === 'string'}
                item={row.returnOrderResponsePath}
                itemRequiresParse={false}
                title='CREATED ORDER'
              />
            </div>
            {row.returnOrder &&
              <div className='beacon-header'>Order</div>
            }
            {row.returnOrder && <div>
                <div className='journey-title'>
                  <span className='beacon-link'>
                    <a className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${getSalesforceUrlByWindowLocation(window.location.href)}/${JSON.parse(row.returnOrder).Id}`}>{JSON.parse(row.returnOrder).Name}</a>
                  </span>
                </div>
              </div>
            }
            {row.returnIags &&
              <div className='beacon-header'>IA GROUPS</div>
            }
            {row.returnIags && <div>
                {JSON.parse(row.returnIags).map(iag => (
                  <div className='journey-title'>
                    <span className='beacon-link'>
                      <a className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${getSalesforceUrlByWindowLocation(window.location.href)}/${iag.Id}`}>{iag.Shipment_Name__c}</a>
                    </span>
                  </div>
                ))}
              </div>
            }
          </div>
          <CustomError closeError={handleCloseReturnError} content={returnError?.content} description={returnError?.description} title={returnError?.title} visible={returnError?.visible} />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
    </>
  }, [props, wrpoNumber, returnError, pdfFile, excelFile, props.row])

  return view
}